import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Container, Row } from 'react-bootstrap';
import { useWebContent } from '../hooks/useWebContent';

const Counter = () => {
  const { data: webContent, isLoading, isError } = useWebContent();
  const ceoSection = webContent?.dataset?.data?.['about-us-downloads-section']?.json_data;

  // Fallback data for counters
  const fallbackCounters = [
    { title: 'Positive Reviews', count: '500000', icon: 'https://via.placeholder.com/50' },
    { title: 'Countries Users', count: '1000000', icon: 'https://via.placeholder.com/50' },
    { title: 'Team Members', count: '150000', icon: 'https://via.placeholder.com/50' },
    { title: 'Total Downloads', count: '100000', icon: 'https://via.placeholder.com/50' },
  ];

  // Dynamic counters from API or fallback
  const counters = useMemo(() => {
    if (isError || !ceoSection || !ceoSection.downloads) {
      return fallbackCounters;
    }
    const mappedCounters = ceoSection.downloads.map(download => ({
      title: download.title,
      count: download.count,
      icon: download.icon || 'https://via.placeholder.com/50',
    }));
    return mappedCounters;
  }, [isError, ceoSection]);

  // Initialize counter values for animation
  const [counterValues, setCounterValues] = useState(new Array(counters.length).fill(0));
  const intervalRef = useRef(null);
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  // Format large numbers (e.g., 11111111111 -> "11.1B")
  const formatNumber = (num) => {
    if (isNaN(num) || num === 0) return '0';
    if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (num >= 1_000) {
      return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const startCounter = () => {
    // Reset counter values
    setCounterValues(new Array(counters.length).fill(0));

    const duration = 2000; // Animation duration in ms
    const intervalTime = 50; // Update every 50ms
    const steps = duration / intervalTime;
    let step = 0;

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setCounterValues(prev => {
        const newValues = counters.map((counter) => {
          const target = parseInt(counter.count, 10) || 0;
          const progress = Math.min(step / steps, 1);
          return Math.floor(target * progress);
        });
        return newValues;
      });

      step++;
      if (step > steps) {
        clearInterval(intervalRef.current);
        hasAnimated.current = true;
      }
    }, intervalTime);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current && !isLoading && counters.length > 0) {
          startCounter();
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
      // Fallback: Check if section is already in view on mount
      const rect = sectionRef.current.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.top <= window.innerHeight * 0.5;
      if (isVisible && !hasAnimated.current && !isLoading && counters.length > 0) {
        startCounter();
      }
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLoading, counters]);

  return (
    <section className="total-download" ref={sectionRef}>
      <Container className="counter-container">
        <Row>
          <div className="col-md-12">
            <ul className="downloadd-block mb-0">
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <li key={i}>
                    <div className="dowmloadd-img">
                      <span className="shimmer" style={{ width: '50px', height: '50px' }} />
                    </div>
                    <div className="downloadd-content">
                      <div className="counter downloadd-counter">
                        <span className="shimmer" style={{ width: '60px', height: '20px' }} />
                      </div>
                      <p className="mb-0 dwn-text">
                        <span className="shimmer" style={{ width: '100px', height: '20px' }} />
                      </p>
                    </div>
                  </li>
                ))
              ) : (
                counters.map((counter, index) => (
                  <li key={index}>
                    <div className="dowmloadd-img">
                      <img src={counter.icon} alt={counter.title} onError={(e) => { e.target.src = 'https://via.placeholder.com/50'; }} />
                    </div>
                    <div className="downloadd-content">
                      <div className="counter downloadd-counter">
                        {formatNumber(counterValues[index] || 0)}
                      </div>
                      <p className="mb-0 dwn-text">{counter.title}</p>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </Row>
      </Container>
    </section>
  );
};

export default Counter;
